/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, CheckCircle2, ArrowRight, Sparkles, Bot, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Analytics } from "@/lib/analytics";

const COURSES = [
  "Digital Marketing Master Program",
  "Meta Ads Course",
  "Google Ads Course",
  "SEO Specialist Course",
  "Graphic Design Course",
  "Video Editing Course",
  "Not sure — recommend me",
];
const QUALIFICATIONS = ["10th / 12th", "Diploma", "Graduate", "Post Graduate", "Working Professional", "Business Owner"];
const GOALS = ["Get a job", "Start freelancing", "Grow my business", "Switch career", "Side income"];
const BUDGETS = ["Under ₹10,000", "₹10,000 – ₹20,000", "₹20,000 – ₹50,000", "Above ₹50,000"];

// Deterministic question flow → 1 final AI call. Fast & cheap.
const STEPS = [
  { key: "name",          label: "Aapka naam kya hai?",              type: "text",   placeholder: "e.g. Rahul Verma" },
  { key: "mobile",        label: "Mobile number?",                   type: "tel",    placeholder: "10-digit mobile", validate: (v) => /^\d{10}$/.test(v) || "Please enter a valid 10-digit mobile" },
  { key: "qualification", label: "Aapki qualification?",             type: "choice", options: QUALIFICATIONS },
  { key: "course_interest", label: "Kaun sa course interest hai?",   type: "choice", options: COURSES },
  { key: "career_goal",   label: "Aapka career goal?",                type: "choice", options: GOALS },
  { key: "city",          label: "Aap kahaan se ho?",                 type: "text",   placeholder: "e.g. Indore" },
  { key: "budget",        label: "Investment ke liye budget?",        type: "choice", options: BUDGETS },
];

const STORE_KEY = "cwk_chat_lead";

export default function AICounselorChat() {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [messages, setMessages] = useState([]);   // {role: 'bot'|'user', text, choices?, ts}
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [aiReply, setAiReply] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const scrollRef = useRef(null);

  // Restore partial chat on mount
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(STORE_KEY) || "{}");
      if (cached.leadId) setLeadId(cached.leadId);
      if (cached.answers) setAnswers(cached.answers);
      if (typeof cached.stepIdx === "number") setStepIdx(cached.stepIdx);
    } catch { /* ignore */ }
  }, []);

  // Init greeting when opened first time
  useEffect(() => {
    if (open && messages.length === 0) {
      setPulse(false);
      const greeting = "Namaste! Main Kamlesh AI hoon 👋. 2 minute mein perfect course suggest kar sakta hoon. Shall we start?";
      setMessages([{ role: "bot", text: greeting, ts: Date.now() }]);
      // Push first question after slight pause
      setTimeout(() => pushBotQuestion(stepIdx), 600);
      Analytics.track?.("chatbot_opened", { source: "floating_button" });
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, submitting, aiReply]);

  const persist = (next) => {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch {/* */}
  };

  const pushBotQuestion = (i) => {
    const s = STEPS[i];
    if (!s) return;
    setMessages((m) => [...m, { role: "bot", text: s.label, choices: s.type === "choice" ? s.options : null, ts: Date.now() }]);
  };

  const saveLeadPartial = async (nextAnswers, step) => {
    try {
      const body = { ...nextAnswers, source: "chatbot", step };
      if (leadId) body.id = leadId;
      const { data } = await api.post("/leads", body);
      if (!leadId && data.id) {
        setLeadId(data.id);
        persist({ leadId: data.id, answers: nextAnswers, stepIdx: step });
      } else {
        persist({ leadId: leadId || data.id, answers: nextAnswers, stepIdx: step });
      }
    } catch (e) {
      // silently ignore — UX must not block
    }
  };

  const submitAnswer = async (rawValue) => {
    const s = STEPS[stepIdx];
    const value = (rawValue ?? input).trim();
    if (!value) return;
    if (s.validate) {
      const ok = s.validate(value);
      if (ok !== true) { setMessages((m) => [...m, { role: "bot", text: ok, ts: Date.now() }]); return; }
    }
    const nextAnswers = { ...answers, [s.key]: value };
    setAnswers(nextAnswers);
    setMessages((m) => [...m, { role: "user", text: value, ts: Date.now() }]);
    setInput("");

    // Save partial after every step (helps recover dropped leads)
    saveLeadPartial(nextAnswers, stepIdx + 1);

    const next = stepIdx + 1;
    if (next < STEPS.length) {
      setStepIdx(next);
      setTimeout(() => pushBotQuestion(next), 450);
    } else {
      // All collected → one AI call for final personalized reply
      setSubmitting(true);
      try {
        const { data } = await api.post("/leads/ai-summary", {
          name: nextAnswers.name,
          mobile: nextAnswers.mobile,
          qualification: nextAnswers.qualification || "",
          course_interest: nextAnswers.course_interest || "",
          career_goal: nextAnswers.career_goal || "",
          city: nextAnswers.city || "",
          budget: nextAnswers.budget || "",
        });
        setAiReply(data);
        setMessages((m) => [...m, { role: "bot", text: data.message, ts: Date.now() }]);
        Analytics.track?.("chatbot_completed", { course: nextAnswers.course_interest, score: data.score });
      } catch {
        setMessages((m) => [...m, { role: "bot", text: `Bahut accha ${nextAnswers.name}! Humara counsellor 30 minutes mein call karega. Tab tak WhatsApp pe message kar sakte ho.`, ts: Date.now() }]);
      } finally {
        setSubmitting(false);
        setDone(true);
        // Persist final
        persist({ leadId, answers: nextAnswers, stepIdx: STEPS.length });
      }
    }
  };

  const reset = () => {
    setMessages([]);
    setStepIdx(0);
    setAnswers({});
    setAiReply(null);
    setDone(false);
    setLeadId(null);
    try { localStorage.removeItem(STORE_KEY); } catch {/*ignore*/}
  };

  const currentStep = STEPS[stepIdx];

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 md:bottom-6 md:right-6 z-40 inline-flex items-center gap-2 bg-gradient-to-br from-[#FF0066] to-pink-600 hover:scale-105 active:scale-95 text-white font-semibold rounded-full pl-3 pr-4 py-3 shadow-[0_8px_30px_-4px_rgba(255,0,102,0.6)] transition-all"
          data-testid="ai-chat-floating-btn"
          aria-label="Open AI counsellor chat"
        >
          <span className="relative flex">
            <Bot className="w-5 h-5" />
            {pulse && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />}
          </span>
          <span className="hidden sm:inline text-sm">Ask Kamlesh AI</span>
        </button>
      )}

      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-4 right-4 left-4 md:left-auto md:bottom-6 md:right-6 z-50 w-auto md:w-[400px] bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl shadow-[#FF0066]/20 overflow-hidden flex flex-col"
          style={{ maxHeight: "min(640px, 85vh)" }}
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#FF0066]/15 via-[#FF0066]/5 to-transparent border-b border-white/10">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF0066] to-pink-600 flex items-center justify-center text-white">
              <Bot className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-white flex items-center gap-1.5">Kamlesh AI <Sparkles className="w-3 h-3 text-amber-400" /></div>
              <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · Counsellor ready
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1" data-testid="ai-chat-close" aria-label="Close chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#050505]">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "bot" && (
                  <div className="w-7 h-7 rounded-full bg-[#FF0066]/15 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-[#FF0066]" />
                  </div>
                )}
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-snug ${m.role === "user" ? "bg-[#FF0066] text-white rounded-br-sm" : "bg-white/5 text-white/90 border border-white/10 rounded-bl-sm"}`}>
                  {m.text}
                  {m.choices && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {m.choices.map((c) => (
                        <button
                          key={c}
                          onClick={() => submitAnswer(c)}
                          className="px-2.5 py-1 rounded-full bg-[#FF0066]/15 hover:bg-[#FF0066]/25 border border-[#FF0066]/30 text-[#FF0066] text-xs font-medium transition"
                          data-testid={`ai-chat-choice-${c.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {submitting && (
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-[#FF0066]/15 flex items-center justify-center"><Bot className="w-4 h-4 text-[#FF0066]" /></div>
                <div className="bg-white/5 border border-white/10 rounded-2xl px-3.5 py-2.5 text-sm text-white/70 inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                </div>
              </div>
            )}
            {done && (
              <div className="mt-3 bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-3 py-3">
                <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold mb-2"><CheckCircle2 className="w-3.5 h-3.5" /> Saved! We will call within 30 min.</div>
                <div className="flex flex-col gap-2">
                  <a href={`https://wa.me/919131341638?text=${encodeURIComponent(`Hi, I just enquired about ${answers.course_interest}. My name is ${answers.name}.`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-semibold rounded-full px-3 py-2" data-testid="ai-chat-wa-cta">
                    Continue on WhatsApp <ArrowRight className="w-3 h-3" />
                  </a>
                  <button onClick={reset} className="text-xs text-white/50 hover:text-white">Start a new chat</button>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          {!done && currentStep && currentStep.type !== "choice" && (
            <div className="border-t border-white/10 bg-[#0a0a0a] px-3 py-2.5 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submitAnswer(); }}
                placeholder={currentStep.placeholder || "Type here…"}
                type={currentStep.type === "tel" ? "tel" : "text"}
                className="flex-1 bg-[#050505] border border-white/15 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF0066]"
                data-testid="ai-chat-input"
                autoFocus
              />
              <button
                onClick={() => submitAnswer()}
                disabled={!input.trim()}
                className="bg-[#FF0066] hover:bg-[#e6005c] disabled:opacity-40 text-white rounded-full p-2.5"
                data-testid="ai-chat-send"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Footer disclaimer */}
          <div className="px-3 py-1.5 text-[9px] text-white/30 text-center border-t border-white/5 bg-[#0a0a0a]">
            By chatting you agree to receive WhatsApp / call from CodeWithKamlesh
          </div>
        </div>
      )}
    </>
  );
}
